<script setup lang="ts">
import { ref, computed } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { taskRecordsApi, type TaskPlanSync } from '@/api/task-records';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// Offers to bring the tasks of a shift in progress in line with the patient's
// task plan: missing tasks are added and pending tasks no longer in the plan
// dropped; recorded tasks never change.
const props = defineProps<{ shiftId: number; sync: TaskPlanSync }>();

const emit = defineEmits<{ synced: [] }>();

const confirming = ref(false);

const { mutate, isPending, error, reset } = useMutation<TaskPlanSync, AxiosError<{ errors?: string }>>({
  mutationFn: () => taskRecordsApi.syncPlan(props.shiftId),
  onSuccess: () => {
    confirming.value = false;
    emit('synced');
  },
});

function count(amount: number, one: string, many: string) {
  return `${amount} ${amount === 1 ? one : many}`;
}

const changes = computed(() => [
  props.sync.toCreate > 0 && count(props.sync.toCreate, 'tarea nueva', 'tareas nuevas'),
  props.sync.toRemove > 0 && count(props.sync.toRemove, 'tarea pendiente a quitar', 'tareas pendientes a quitar'),
].filter(Boolean).join(' y '));

const errorMessage = computed(() => error.value && (
  error.value.response?.data?.errors || 'No se pudieron actualizar las tareas. Intenta nuevamente.'
));

function openConfirmation() {
  reset();
  confirming.value = true;
}
</script>

<template>
  <v-alert
    type="info"
    variant="tonal"
    density="compact"
    class="mx-2 mt-2"
  >
    El plan de tareas del paciente cambió: {{ changes }}.
    <template #append>
      <v-btn
        color="primary"
        variant="flat"
        size="small"
        @click="openConfirmation"
      >
        Actualizar tareas
      </v-btn>
    </template>
  </v-alert>
  <responsive-dialog
    v-model="confirming"
    title="Actualizar tareas del turno"
    max-width="480"
    :mobile-fullscreen="false"
  >
    <p>Se aplicará el plan de tareas actual del paciente: {{ changes }}.</p>
    <p class="mt-2">
      Las tareas ya registradas como completadas o incompletas no cambian.
    </p>
    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      density="compact"
      class="mt-4"
    >
      {{ errorMessage }}
    </v-alert>
    <template #actions>
      <v-btn
        text="Cancelar"
        :disabled="isPending"
        @click="confirming = false"
      />
      <v-btn
        color="primary"
        variant="flat"
        text="Actualizar"
        :loading="isPending"
        @click="mutate()"
      />
    </template>
  </responsive-dialog>
</template>
